/**
 * CreatorArtifactsTable - Artifacts table for individual creators
 * 
 * Shows the creator's linked artifacts with their metrics in a table format.
 * Phase 5: Added behavioral signals + pensieve columns.
 */

import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, Search, ArrowUpDown, ArrowUp, ArrowDown, Users, Target, TrendingUp, CheckCircle, Pencil } from "lucide-react";
import type { CreatorArtifact } from "@/hooks/useCreatorAnalytics";
import { EditTrainingArtifactDialog } from "@/components/training/EditTrainingArtifactDialog";
import type { TrainingArtifact } from "@/types/training";
import { useQueryClient } from "@tanstack/react-query";

type SortField = 'title' | 'type' | 'sessions' | 'completions' | 'technicians' | 'efficiency' | 'confidence' | 'behavioral' | 'pensieve';
type SortDir = 'asc' | 'desc';

interface CreatorArtifactsTableProps {
  artifacts: CreatorArtifact[];
  creatorName?: string;
}

export function CreatorArtifactsTable({ artifacts, creatorName }: CreatorArtifactsTableProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>('sessions');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Filter and sort
  const filteredAndSorted = useMemo(() => {
    let result = [...artifacts];

    // Search filter
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(a => 
        a.artifact?.title?.toLowerCase().includes(lowerSearch) ||
        a.artifact?.provider_name?.toLowerCase().includes(lowerSearch)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal: number | string = 0;
      let bVal: number | string = 0;

      switch (sortField) {
        case 'title':
          aVal = (a.artifact?.title || '').toLowerCase();
          bVal = (b.artifact?.title || '').toLowerCase();
          break;
        case 'type':
          aVal = (a.artifact?.artifact_type || '').toLowerCase();
          bVal = (b.artifact?.artifact_type || '').toLowerCase();
          break;
        case 'sessions':
          aVal = a.metrics?.total_sessions || 0;
          bVal = b.metrics?.total_sessions || 0;
          break;
        case 'completions':
          aVal = (a.metrics?.verified_completions || 0) + (a.metrics?.likely_completions || 0);
          bVal = (b.metrics?.verified_completions || 0) + (b.metrics?.likely_completions || 0);
          break;
        case 'technicians':
          aVal = a.metrics?.sample_technicians || 0;
          bVal = b.metrics?.sample_technicians || 0;
          break;
        case 'efficiency':
          aVal = a.metrics?.avg_efficiency_delta_percent ?? -999;
          bVal = b.metrics?.avg_efficiency_delta_percent ?? -999;
          break;
        case 'confidence':
          aVal = a.metrics?.avg_confidence_delta ?? -999;
          bVal = b.metrics?.avg_confidence_delta ?? -999;
          break;
        case 'behavioral':
          aVal = meetsThreshold(a) ? ((a.metrics?.behavioral_notes_rate || 0) + (a.metrics?.behavioral_progress_rate || 0)) / 2 : -999;
          bVal = meetsThreshold(b) ? ((b.metrics?.behavioral_notes_rate || 0) + (b.metrics?.behavioral_progress_rate || 0)) / 2 : -999;
          break;
        case 'pensieve':
          aVal = meetsThreshold(a) ? (a.metrics?.avg_pensieve_score || -999) : -999;
          bVal = meetsThreshold(b) ? (b.metrics?.avg_pensieve_score || -999) : -999;
          break;
      }

      if (typeof aVal === 'string') {
        return sortDir === 'asc' 
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      
      return sortDir === 'asc' 
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

    return result;
  }, [artifacts, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDir === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const formatNumber = (n: number | null | undefined) => {
    if (n === null || n === undefined) return '—';
    return n.toLocaleString();
  };

  const formatPercent = (n: number | null | undefined) => {
    if (n === null || n === undefined) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
  };

  const formatDelta = (n: number | null | undefined) => {
    if (n === null || n === undefined) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(2)}`;
  };

  // Check if metrics meet privacy thresholds
  const meetsThreshold = (artifact: CreatorArtifact) => {
    if (!artifact.metrics) return false;
    return artifact.metrics.sample_shops >= 3 && artifact.metrics.sample_technicians >= 5;
  };

  // Format behavioral rate as "notes% / progress%"
  const formatBehavioral = (artifact: CreatorArtifact) => {
    if (!meetsThreshold(artifact)) return '—';
    const m = artifact.metrics!;
    const notesRate = m.behavioral_notes_rate || 0;
    const progressRate = m.behavioral_progress_rate || 0;
    if (notesRate === 0 && progressRate === 0) return '—';
    return `${Math.round(notesRate * 100)}% / ${Math.round(progressRate * 100)}%`;
  };

  // Format pensieve score with sample count
  const formatPensieve = (artifact: CreatorArtifact) => {
    if (!meetsThreshold(artifact)) return '—';
    const m = artifact.metrics!;
    if (!m.pensieve_sample_count || m.pensieve_sample_count === 0) return '—';
    return `${Number(m.avg_pensieve_score).toFixed(1)} (n=${m.pensieve_sample_count})`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {creatorName ? `${creatorName}'s Training Artifacts` : 'Your Training Artifacts'}
            </CardTitle>
            <CardDescription>
              {filteredAndSorted.length} of {artifacts.length} artifacts
            </CardDescription>
          </div>
        </div>
        
        {/* Search */}
        {artifacts.length > 3 && (
          <div className="flex gap-3 pt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search artifacts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button variant="ghost" size="sm" onClick={() => handleSort('title')} className="h-auto p-0 font-medium">
                    Artifact <SortIcon field="title" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button variant="ghost" size="sm" onClick={() => handleSort('type')} className="h-auto p-0 font-medium">
                    Type <SortIcon field="type" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('sessions')} className="h-auto p-0 font-medium">
                    <Target className="h-3 w-3 mr-1" />
                    Sessions <SortIcon field="sessions" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('completions')} className="h-auto p-0 font-medium">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Completions <SortIcon field="completions" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('technicians')} className="h-auto p-0 font-medium">
                    <Users className="h-3 w-3 mr-1" />
                    Techs <SortIcon field="technicians" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('efficiency')} className="h-auto p-0 font-medium">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Efficiency Δ <SortIcon field="efficiency" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('confidence')} className="h-auto p-0 font-medium">
                    Confidence Δ <SortIcon field="confidence" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('behavioral')} className="h-auto p-0 font-medium">
                    Behavioral <SortIcon field="behavioral" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleSort('pensieve')} className="h-auto p-0 font-medium">
                    Pensieve <SortIcon field="pensieve" />
                  </Button>
                </TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    No artifacts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSorted.map(artifact => {
                  const hasMetrics = meetsThreshold(artifact);
                  
                  return (
                    <TableRow key={artifact.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{artifact.artifact?.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {artifact.artifact?.provider_name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {artifact.artifact?.artifact_type}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">
                          {artifact.artifact?.skill_depth}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {hasMetrics ? formatNumber(artifact.metrics?.total_sessions) : (
                          <span className="text-muted-foreground text-xs">Not enough data</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {hasMetrics ? formatNumber(
                          (artifact.metrics?.verified_completions || 0) + 
                          (artifact.metrics?.likely_completions || 0)
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {hasMetrics ? formatNumber(artifact.metrics?.sample_technicians) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {hasMetrics ? (
                          <span className={
                            artifact.metrics?.avg_efficiency_delta_percent != null
                              ? artifact.metrics.avg_efficiency_delta_percent > 0 
                                ? 'text-green-600' 
                                : artifact.metrics.avg_efficiency_delta_percent < 0 
                                  ? 'text-red-600' 
                                  : ''
                              : ''
                          }>
                            {formatPercent(artifact.metrics?.avg_efficiency_delta_percent)}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {hasMetrics ? (
                          <span className={
                            artifact.metrics?.avg_confidence_delta != null
                              ? artifact.metrics.avg_confidence_delta > 0 
                                ? 'text-green-600' 
                                : artifact.metrics.avg_confidence_delta < 0 
                                  ? 'text-red-600' 
                                  : ''
                              : ''
                          }>
                          {formatDelta(artifact.metrics?.avg_confidence_delta)}
                          </span>
                        ) : '—'}
                      </TableCell>
                      {/* Phase 5: Behavioral Signals column */}
                      <TableCell className="text-right font-mono text-sm">
                        {formatBehavioral(artifact)}
                      </TableCell>
                      {/* Phase 5: Pensieve column */}
                      <TableCell className="text-right font-mono text-sm">
                        {formatPensieve(artifact)}
                      </TableCell>
                      <TableCell>
                        {artifact.artifact && (
                          <EditTrainingArtifactDialog
                            artifact={artifact.artifact as TrainingArtifact}
                            onUpdated={() => {
                              queryClient.invalidateQueries({ queryKey: ['creator-artifacts'] });
                            }}
                            trigger={
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Privacy note */}
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Metrics require 3+ shops and 5+ technicians to display for privacy protection.
          Behavioral = notes rate / progress rate.
        </p>
      </CardContent>
    </Card>
  );
}
